/**
 * Icon Library
 * 
 * Centralized icon library for sidebar customization and UI components.
 * Uses only available Material-UI icons organized by category:
 * - Navigation & Dashboard
 * - Commerce & Orders
 * - Inventory & Products
 * - Logistics & Transport
 * - Warehouses & Locations
 * - Users & Teams
 * - Analytics & Reports
 * - System & Configuration
 * 
 * @module utils/iconLibrary
 */

import {
  // Navigation & Dashboard
  Dashboard as DashboardIcon,
  Home as HomeIcon,
  Menu as MenuIcon,
  Apps as AppsIcon,
  
  // Commerce & Orders
  ShoppingCart as ShoppingCartIcon,
  ShoppingBag as ShoppingBagIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  
  // Inventory & Products
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  Label as LabelIcon,
  QrCode as QrCodeIcon,
  
  // Logistics & Transport
  LocalShipping as LocalShippingIcon,
  DeliveryDining as DeliveryDiningIcon,
  FlightTakeoff as FlightTakeoffIcon,
  
  // Warehouses & Locations
  Warehouse as WarehouseIcon,
  Store as StoreIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  
  // Users & Teams
  People as PeopleIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  SupervisorAccount as SupervisorAccountIcon,
  
  // Analytics & Reports
  Analytics as AnalyticsIcon,
  Assessment as AssessmentIcon,
  BarChart as BarChartIcon,
  TrendingUp as TrendingUpIcon,
  
  // System & Configuration
  Settings as SettingsIcon,
  Build as BuildIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon
} from '@mui/icons-material';

export const ICON_CATEGORIES = {
  navigation: {
    name: 'Navigation & Dashboard',
    icons: [
      { key: 'dashboard', name: 'Dashboard', component: DashboardIcon },
      { key: 'home', name: 'Home', component: HomeIcon },
      { key: 'menu', name: 'Menu', component: MenuIcon },
      { key: 'apps', name: 'Applications', component: AppsIcon }
    ]
  },
  commerce: {
    name: 'Commerce & Orders',
    icons: [
      { key: 'shopping_cart', name: 'Cart', component: ShoppingCartIcon },
      { key: 'shopping_bag', name: 'Orders', component: ShoppingBagIcon },
      { key: 'receipt', name: 'Invoices', component: ReceiptIcon },
      { key: 'payment', name: 'Payments', component: PaymentIcon }
    ]
  },
  inventory: {
    name: 'Inventory & Products',
    icons: [
      { key: 'inventory_items', name: 'Inventory', component: InventoryIcon },
      { key: 'category', name: 'Categories', component: CategoryIcon },
      { key: 'label', name: 'Labels', component: LabelIcon },
      { key: 'qr_code', name: 'QR Code', component: QrCodeIcon }
    ]
  },
  logistics: {
    name: 'Logistics & Transport',
    icons: [
      { key: 'shipping', name: 'Shipping', component: LocalShippingIcon },
      { key: 'delivery', name: 'Delivery', component: DeliveryDiningIcon },
      { key: 'flight', name: 'Air Transport', component: FlightTakeoffIcon }
    ]
  },
  warehouse: {
    name: 'Warehouses & Locations',
    icons: [
      { key: 'warehouse', name: 'Warehouse', component: WarehouseIcon },
      { key: 'store', name: 'Store', component: StoreIcon },
      { key: 'business', name: 'Business', component: BusinessIcon },
      { key: 'location', name: 'Location', component: LocationOnIcon }
    ]
  },
  users: {
    name: 'Users & Teams',
    icons: [
      { key: 'people', name: 'Team', component: PeopleIcon },
      { key: 'person', name: 'User', component: PersonIcon },
      { key: 'group', name: 'Group', component: GroupIcon },
      { key: 'supervisor', name: 'Supervisor', component: SupervisorAccountIcon }
    ]
  },
  analytics: {
    name: 'Analytics & Reports',
    icons: [
      { key: 'analytics', name: 'Analytics', component: AnalyticsIcon },
      { key: 'assessment', name: 'Evaluation', component: AssessmentIcon },
      { key: 'bar_chart', name: 'Chart', component: BarChartIcon },
      { key: 'trending_up', name: 'Trends', component: TrendingUpIcon }
    ]
  },
  system: {
    name: 'System & Configuration',
    icons: [
      { key: 'settings', name: 'Settings', component: SettingsIcon },
      { key: 'build', name: 'Tools', component: BuildIcon },
      { key: 'security', name: 'Security', component: SecurityIcon },
      { key: 'notifications', name: 'Notifications', component: NotificationsIcon }
    ]
  }
};

export const getAllIcons = () => {
  const allIcons = [];
  Object.values(ICON_CATEGORIES).forEach(category => {
    allIcons.push(...category.icons);
  });
  return allIcons;
};

export const searchIcons = (query) => {
  if (!query) return getAllIcons();
  
  const searchTerm = query.toLowerCase();
  return getAllIcons().filter(icon => 
    icon.name.toLowerCase().includes(searchTerm) ||
    icon.key.toLowerCase().includes(searchTerm)
  );
};

export const getIconByKey = (key) => {
  return getAllIcons().find(icon => icon.key === key);
};

export const getIconComponent = (key) => {
  const icon = getIconByKey(key);
  return icon ? icon.component : DashboardIcon; // Fallback to Dashboard
};

